import { useIntl } from 'react-intl';
import { Toast } from 'sonata-react-components';

import { GroupForm } from '../GroupForm/GroupForm';
import { groupingMessages } from '../groupingMessages';
import { AddMembersRow } from './AddMembersRow';
import { GroupMembersList } from './GroupMembersList';

import type { GroupCapabilities, GroupFamily, GroupNameError } from '../../domain/grouping.types';
import type { GroupingItem } from '../../hooks/useGroupingItems';
import type { UseGroupEditDraftResult } from './useGroupEditDraft';

export interface EditGroupDetailProps {
  /** What may be changed: cartographic groups keep name and members read-only. */
  capabilities: GroupCapabilities;
  draftState: UseGroupEditDraftResult & { draft: NonNullable<UseGroupEditDraftResult['draft']> };
  family: GroupFamily;
  items: GroupingItem[];
  nameError: GroupNameError | null;
}

/** Detail step of "Edit Groups": rename, recolor, members and pending deletion warning (per group capabilities). */
export function EditGroupDetail({
  capabilities,
  draftState,
  family,
  items,
  nameError
}: Readonly<EditGroupDetailProps>) {
  const { formatMessage } = useIntl();
  const { addMembers, draft, removeMembers, setColor, setName } = draftState;
  const members = items.filter(({ id }) => draft.memberIds.includes(id));
  const candidates = items.filter(({ id }) => !draft.memberIds.includes(id));

  let warning: string | null = null;
  if (draft.deleteGroup) {
    warning = formatMessage(groupingMessages.deleteGroupWarning);
  } else if (members.length === 0) {
    warning = formatMessage(groupingMessages.emptyGroupWarning);
  }

  return (
    <div className="editGroupsModal__detail">
      {warning && <Toast autoClose={false} icon="fal fa-exclamation-triangle" text={warning} type="warning" />}
      <GroupForm
        color={draft.color}
        id="editGroupsModal"
        name={draft.name}
        nameError={nameError}
        nameReadOnly={!capabilities.canRename}
        onColorChange={setColor}
        onNameChange={setName}
      />
      {members.length > 0 && (
        <GroupMembersList members={members} onRemove={removeMembers} readOnly={!capabilities.canEditMembers} />
      )}
      {capabilities.canEditMembers && <AddMembersRow candidates={candidates} family={family} onAdd={addMembers} />}
    </div>
  );
}
